import { useDispatch } from 'react-redux';
import { formatMoneyDollars, formatNumber } from '../../../utils';
import ToggleButton from '../../../components/toggle-button/ToggleButton';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import ScreenReaderOnly from '../../../accessibility/ScreenReaderOnly';
import styles from './input-amount.module.less';
import { ErrorFieldMessage, InputTextLike } from '@sb1/ffe-form-react';
import { useEffect, useState } from 'react';
import { clearProjectErrors } from '../../project-actions';

const InternationalInputAmount = ({
    projectId,
    projectProfile = {},
    onAmountChange,
    trackingCategory = 'support',
    trackingAction = 'checkout-international-suggestion',
    info,
    initialAmount,
    ...props
}) => {
    const [formAmount, setFormAmount] = useState(initialAmount);

    const dispatch = useDispatch();
    const [conversionRate, setConversionRate] = useState(11.08319);
    const [calculatedNok, setCalculatedNok] = useState(0);

    useEffect(() => {
        fetch('https://data.norges-bank.no/api/data/EXR/B.USD.NOK.SP?format=sdmx-json&lastNObservations=1&locale=no')
            .then((res) => res.json())
            .then((json) => {
                const data = json?.data?.dataSets?.[0]?.series?.['0:0:0:0']?.observations?.[0];
                if (data) {
                    setConversionRate(parseFloat(data));
                }
            })
            .catch(() => {
                setConversionRate(11.1442);
            });
    }, []);

    const giveAmountChangeHandler = (event) => {
        let amount = event.target.value.replace(/[^0-9.]/g, '').match(/^[0-9]+/);
        if (amount) {
            amount = parseInt(amount, 10);
        }
        const amountConverted = amount * conversionRate;
        setCalculatedNok(amountConverted);
        if (onAmountChange) {
            onAmountChange(amountConverted);
        }
        setFormAmount(amount);
        dispatch(clearProjectErrors());
    };

    const setAmount = (amount) => {
        trackEvent(trackingCategory, trackingAction, { amount, 'project-id': projectId });
        const amountConverted = amount * conversionRate;
        setCalculatedNok(amountConverted);
        if (onAmountChange) {
            onAmountChange(amountConverted);
        }
        setFormAmount(amount);
    };

    const lowAmount = 5;
    const mediumAmount = 10;
    const highAmount = 50;
    const highestAmount = 100;

    const error = props?.errors?.global || props?.errors?.amount || '';

    return (
        <div>
            {!projectProfile?.hideDefaultPaymentAmounts && (
                <fieldset aria-labelledby="suggested-amounts">
                    <ScreenReaderOnly tag={'legend'} id="suggested-amounts">
                        Suggested amounts
                    </ScreenReaderOnly>
                    <div className={styles.inputAmountContainer}>
                        <ToggleButton
                            selected={formAmount === lowAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, lowAmount)}
                        >
                            {formatMoneyDollars(lowAmount)}
                        </ToggleButton>
                        <ToggleButton
                            selected={formAmount === mediumAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, mediumAmount)}
                        >
                            {formatMoneyDollars(mediumAmount)}
                        </ToggleButton>
                        <ToggleButton
                            selected={formAmount === highAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, highAmount)}
                        >
                            {formatMoneyDollars(highAmount)}
                        </ToggleButton>
                        <ToggleButton
                            selected={formAmount === highestAmount}
                            className={styles.predefinedAmountButton}
                            onClick={setAmount.bind(this, highestAmount)}
                        >
                            {formatMoneyDollars(highestAmount)}
                        </ToggleButton>
                    </div>
                </fieldset>
            )}

            <div className={styles.inputAmountWrapper}>
                <ScreenReaderOnly id="inputAmountLabel">
                    How much money would you like to support the fundraiser with?
                </ScreenReaderOnly>
                <div className={styles.inputAmountPrefix}>$</div>
                <InputTextLike
                    className={styles.inputAmount}
                    aria-labelledby="inputAmountLabel"
                    onChange={giveAmountChangeHandler}
                    onBlur={(e) => giveAmountChangeHandler(e, true)}
                    value={formAmount}
                    autoFocus
                    inputMode="number"
                    type="number"
                    pattern="[0-9]*"
                    aria-describedby="inputAmountFormError"
                    ariaLabel={'Amount to support the fundraiser with'}
                />
            </div>
            {info && <p className={styles.info}>{info}</p>}
            {error && (
                <ErrorFieldMessage id="inputAmountFormError" className={styles.formError}>
                    {error}
                </ErrorFieldMessage>
            )}

            <div className={styles.conversionInfo}>
                This equals
                <div className={styles.nokAmount}>{formatNumber(calculatedNok, 2)}&nbsp;NOK</div>
                <div>
                    The currency conversion is based on <div>$1 = {conversionRate}&nbsp;NOK</div> which is the current
                    available conversion rate. Conversion rates are subject to change.
                </div>
            </div>
        </div>
    );
};

export default InternationalInputAmount;
